// Define the core base URL prefix
const BASE_URL_PREFIX = 'https://apps.fingergiant.com/pos-saas';
// const BASE_URL_PREFIX = 'http://localhost:8000';

const AUTH_TYPE = 'Bearer';

/**
 * Generic function to make API calls.
 * @param {string} endpoint - The API endpoint (e.g., "/api/pos/v1/product/all").
 * @param {string} method - HTTP method (e.g., "GET", "POST", "DELETE").
 * @param {object} body - Request body for POST/PUT methods.
 * @param {string} token - Authentication token.
 * @param {string} shopCode - The selected shop code.
 * @returns {Promise<object>} - The JSON response from the API.
 */
const fetchData = async (endpoint, method = 'GET', body = null, token = '', shopCode = '') => {
  try {
    let url = `${BASE_URL_PREFIX}/${shopCode}/${endpoint}`;
    
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `${AUTH_TYPE} ${token}`;
    }

    const config = {
      method,
      headers,
    };
    if (body) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(url, config);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Something went wrong');
    }
    return response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

/**
 * Function to download files (CSV, Excel, etc.)
 * @param {string} endpoint - The API endpoint for download.
 * @param {string} method - HTTP method (default: "GET").
 * @param {object} params - Query parameters for the request.
 * @param {string} token - Authentication token.
 * @param {string} shopCode - The selected shop code.
 * @param {string} filename - The filename for the downloaded file.
 * @returns {Promise<void>}
 */
const downloadFile = async (endpoint, method = 'GET', params = {}, token = '', shopCode = '', filename = 'download') => {
  try {
    let url = `${BASE_URL_PREFIX}/${shopCode}/${endpoint}`;
    
    // Add query parameters if provided
    if (Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    
    const headers = {
      'Accept': '*/*',
    };
    if (token) {
      headers['Authorization'] = `${AUTH_TYPE} ${token}`;
    }

    const config = {
      method,
      headers,
    };

    const response = await fetch(url, config);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Download failed');
    }

    // Get the blob from the response
    const blob = await response.blob();
    
    // Create a download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    
    // Trigger the download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export { fetchData, downloadFile, BASE_URL_PREFIX };
